import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Calendar, Trash2, Edit, Music, Camera, Mic, Video, Disc, FileAudio, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Entry type options with icons
const ENTRY_TYPES = [
  { value: 'single', label: 'Single Release', icon: Music },
  { value: 'ep', label: 'EP Release', icon: Disc },
  { value: 'album', label: 'Album Release', icon: Disc },
  { value: 'mixtape', label: 'Mixtape Release', icon: FileAudio },
  { value: 'session', label: 'Studio Session', icon: Mic },
  { value: 'photoshoot', label: 'Photo Shoot', icon: Camera },
  { value: 'video', label: 'Video Shoot', icon: Video },
  { value: 'other', label: 'Other', icon: Calendar },
];

const getEntryIcon = (type) => {
  const entry = ENTRY_TYPES.find(e => e.value === type);
  if (entry) {
    const IconComponent = entry.icon;
    return <IconComponent className="w-5 h-5" />;
  }
  return <Calendar className="w-5 h-5" />;
};

const getEntryColor = (type) => {
  switch (type) {
    case 'single': return 'bg-purple-500/20 text-purple-500 border-purple-500/30';
    case 'ep': return 'bg-blue-500/20 text-blue-500 border-blue-500/30';
    case 'album': return 'bg-green-500/20 text-green-500 border-green-500/30';
    case 'mixtape': return 'bg-orange-500/20 text-orange-500 border-orange-500/30';
    case 'session': return 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30';
    case 'photoshoot': return 'bg-pink-500/20 text-pink-500 border-pink-500/30';
    case 'video': return 'bg-red-500/20 text-red-500 border-red-500/30';
    default: return 'bg-gray-500/20 text-gray-500 border-gray-500/30';
  }
};

const Diary = ({ user, selectedArtist }) => {
  const [entries, setEntries] = useState([]);
  const [artists, setArtists] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' = earliest first, 'desc' = latest first
  const [formData, setFormData] = useState({
    title: '',
    entry_type: 'single',
    date: '',
    notes: '',
    artist_id: '',
  });

  useEffect(() => {
    fetchEntries();
    if (user?.role === 'manager' || user?.role === 'admin') {
      fetchArtists();
    }
  }, [selectedArtist]);

  const fetchEntries = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = selectedArtist ? `?artist_id=${selectedArtist}` : '';
      const response = await axios.get(`${API}/diary${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to fetch diary entries');
    }
  };

  const fetchArtists = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/artists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArtists(response.data);
    } catch (error) {
      
      // Silent fail - artists list is supplementary
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        title: formData.title,
        entry_type: formData.entry_type,
        date: new Date(formData.date).toISOString(),
        notes: formData.notes,
        artist_id: formData.artist_id || selectedArtist || null,
      };
      
      if (editingEntry) {
        await axios.put(`${API}/diary/${editingEntry.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Entry updated successfully!');
      } else {
        await axios.post(`${API}/diary`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Entry created successfully!');
      }
      
      setOpen(false);
      setEditingEntry(null);
      setFormData({ title: '', entry_type: 'single', date: '', notes: '', artist_id: '' });
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingEntry ? 'update' : 'create'} entry`);
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setFormData({
      title: entry.title,
      entry_type: entry.entry_type || 'other',
      date: new Date(entry.date).toISOString().slice(0, 10),
      notes: entry.notes || '',
      artist_id: entry.artist_id || '',
    });
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
    setEditingEntry(null);
    setFormData({ title: '', entry_type: 'single', date: '', notes: '', artist_id: '' });
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/diary/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Entry deleted!');
      await fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    }
  };

  // Sort and group entries by month
  const sortedEntries = [...entries].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const groupedEntries = sortedEntries.reduce((groups, entry) => {
    const date = new Date(entry.date);
    const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (!groups[monthYear]) {
      groups[monthYear] = [];
    }
    groups[monthYear].push(entry);
    return groups;
  }, {});

  // Sort the month groups
  const sortedMonthKeys = Object.keys(groupedEntries).sort((a, b) => {
    const dateA = new Date(a);
    const dateB = new Date(b);
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  return (
    <div className="space-y-8" data-testid="diary-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-manrope font-extrabold text-3xl md:text-4xl text-foreground">Diary</h1>
          <p className="text-muted-foreground text-base mt-2">Sessions, shoots, releases and more</p>
        </div>
        
        <div className="flex gap-2">
          {/* Sort Toggle Button */}
          <Button
            variant="outline"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="border-border hover:bg-muted"
            data-testid="sort-diary-button"
          >
            {sortOrder === 'asc' ? (
              <>
                <ArrowUp className="w-4 h-4 mr-2" />
                Earliest First
              </>
            ) : (
              <>
                <ArrowDown className="w-4 h-4 mr-2" />
                Latest First
              </>
            )}
          </Button>
          
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(124,58,237,0.3)]"
                data-testid="add-diary-entry-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Entry
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-card border-border/50 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-manrope font-bold text-2xl">
                {editingEntry ? 'Edit Entry' : 'Add Entry'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="entry_type">Type</Label>
                <select
                  id="entry_type"
                  data-testid="diary-type-select"
                  value={formData.entry_type}
                  onChange={(e) => setFormData({ ...formData, entry_type: e.target.value })}
                  className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                >
                  {ENTRY_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  data-testid="diary-title-input"
                  placeholder="e.g., Album Recording Session, Press Photos"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="bg-input/50 border-border rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  data-testid="diary-date-input"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  className="bg-input/50 border-border rounded-xl"
                />
              </div>

              {(user?.role === 'manager' || user?.role === 'admin') && (
                <div className="space-y-2">
                  <Label htmlFor="artist_id">Artist</Label>
                  <select
                    id="artist_id"
                    data-testid="diary-artist-select"
                    value={formData.artist_id || selectedArtist || ''}
                    onChange={(e) => setFormData({ ...formData, artist_id: e.target.value })}
                    className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                  >
                    <option value="">Select artist (optional)</option>
                    {artists.map((artist) => (
                      <option key={artist.id} value={artist.id}>
                        {artist.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  data-testid="diary-notes-input"
                  placeholder="Any additional details..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-input/50 border-border rounded-xl min-h-24"
                />
              </div>

              <div className="flex gap-2">
                {editingEntry && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  type="submit"
                  data-testid="submit-diary-button"
                  className={`${editingEntry ? 'flex-1' : 'w-full'} bg-primary hover:bg-primary/90`}
                >
                  {editingEntry ? 'Update Entry' : 'Add Entry'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {entries.length === 0 ? (
        <Card className="bg-card border-border/50 p-12 text-center">
          <p className="text-muted-foreground">No diary entries yet. Add your first entry!</p>
        </Card>
      ) : (
        <div className="space-y-8">
          {sortedMonthKeys.map((monthYear) => (
            <div key={monthYear}>
              <h2 className="font-manrope font-bold text-lg text-muted-foreground mb-4 sticky top-0 bg-background py-2">
                {monthYear}
              </h2>
              <div className="grid gap-4">
                {groupedEntries[monthYear].map((entry) => (
                  <Card
                    key={entry.id}
                    className="bg-card border-border/50 p-4 hover:border-primary/50 transition-colors"
                    data-testid={`diary-entry-${entry.id}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getEntryColor(entry.entry_type)}`}>
                        {getEntryIcon(entry.entry_type)}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-manrope font-semibold text-foreground truncate">
                              {entry.title}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getEntryColor(entry.entry_type)}`}>
                                {ENTRY_TYPES.find(t => t.value === entry.entry_type)?.label || 'Other'}
                              </span>
                              <span className="text-xs text-muted-foreground font-mono">
                                {new Date(entry.date).toLocaleDateString('en-GB', { 
                                  weekday: 'short',
                                  day: 'numeric',
                                  month: 'short'
                                })}
                              </span>
                            </div>
                            {entry.notes && (
                              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                                {entry.notes}
                              </p>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex gap-1 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditEntry(entry)}
                              className="h-8 w-8 p-0"
                              data-testid={`edit-diary-${entry.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteEntry(entry.id)}
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                              data-testid={`delete-diary-${entry.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Diary;
